<div class="map-favorite" [style.height]="height">

  <!-- Labels button (saved favorites only) -->
  <div *ngIf="!minimal && labelsEditable && hasLabelSource" class="map-favorite-toolbar">
    <button type="button" class="btn btn-sm"
            [class.btn-success]="!labelsEditMode" [class.btn-warning]="labelsEditMode"
            (click)="toggleLabelEditing()">
      {{ labelsEditMode ? 'Done Editing Labels' : 'Labels' }}
    </button>
  </div>

  <google-map class="map-favorite-map"
              [center]="initialCenter" [zoom]="initialZoom" [mapTypeId]="mapTypeId"
              [options]="{ draggable: true, fullscreenControl: true }"
              [height]="'100%'" [width]="'100%'"
              (mapInitialized)="onMapInitialized($event)"
              (idle)="onMapIdle()">
    <map-marker #marker="mapMarker" *ngFor="let item of geoMapItems; trackBy: trackByKey"
                [label]="item.labelOptions"
                [options]="{ icon: item.icon }"
                [position]="{ lat: item.gpsLat, lng: item.gpsLong }"
                (mapClick)="openPopup(mapInfoWindow, marker, item)">
      <map-info-window #mapInfoWindow="mapInfoWindow" class="kig-pin-popup-host" (closeclick)="onPopupClosed(item)">
        <ng-container *ngTemplateOutlet="pinPopup; context: { item: item }"></ng-container>
      </map-info-window>
    </map-marker>
  </google-map>

  <!-- Labels. The map component's overlay moves this layer into the map's pane, so the
       labels pan and zoom with the map; positions are pixels in that pane. Labels only
       take pointer events while they are being edited. -->
  <div #labelLayer class="label-layer" [class.editing]="labelsEditMode">
    <svg class="label-arrows">
      <defs>
        <marker *ngFor="let arrow of labelArrows; let i = index; trackBy: trackByIndex" [attr.id]="'mfArrow' + instanceId + '_' + i"
                viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" [attr.fill]="arrow.color"></path>
        </marker>
      </defs>
      <line *ngFor="let arrow of labelArrows; let i = index; trackBy: trackByIndex"
            [attr.x1]="arrow.x1" [attr.y1]="arrow.y1" [attr.x2]="arrow.x2" [attr.y2]="arrow.y2"
            [attr.stroke]="arrow.color" stroke-width="2"
            [attr.marker-end]="'url(#mfArrow' + instanceId + '_' + i + ')'"></line>
    </svg>

    <ng-container *ngFor="let view of labelViews; trackBy: trackByLabelView">
      <div class="map-label"
           [class.region]="view.isRegion"
           [class.plain]="view.isPlain"
           [attr.data-label-id]="view.label.id"
           [ngStyle]="view.style"
           (pointerdown)="startLabelDrag($event, view.label, 'box')">
        <div *ngIf="view.label.text" class="label-text">{{ view.label.text }}</div>
        <div *ngIf="view.label.isDynamic" class="label-value" [ngStyle]="view.valueStyle" [title]="view.valueTooltip">
          {{ view.valueText }}
        </div>
        <div *ngIf="labelsEditMode && view.isRegion" class="resize-handle"
             (pointerdown)="startLabelDrag($event, view.label, 'resize')"></div>
      </div>

      <div *ngIf="labelsEditMode && view.target" class="target-handle"
           [style.left.px]="view.target.x" [style.top.px]="view.target.y"
           (pointerdown)="startLabelDrag($event, view.label, 'target')"></div>
    </ng-container>
  </div>
</div>


<!-- Pin info window. The header is colored from the pin's own GPS-source color; the
     .kig-pin-popup styles in src/sass/styles.scss strip Google's default padding so the
     header reaches the popup edges. Dragging the header moves Google's whole info-window
     container (named by cdkDragRootElement); a leader line is then drawn on the map from
     the pin to the popup (CommonShared.updatePinPopupLeader). -->
<ng-template #pinPopup let-item="item">
  <div class="kig-pin-popup" cdkDrag
       [class.kig-pin-popup-detached]="item.dragOffset?.x || item.dragOffset?.y"
       [cdkDragRootElement]="item.dragRootSelector"
       [cdkDragFreeDragPosition]="item.dragOffset"
       (cdkDragStarted)="common.pinPopupDragStarted($event.source.getRootElement())"
       (cdkDragEnded)="onPopupDragEnded(item, $event)">
    <div class="kig-pin-popup-header" cdkDragHandle [style.color]="item.headerColors?.bar">
      <h5 title="{{ item.sensorRef }}">{{ item.sensorRef }}</h5>
    </div>

    <div class="kig-pin-popup-body">
      <div style="margin-bottom: 10px;">
        <span style="color: #1c4966;">Reading Date: </span>
        <span style="color: black;">{{ item.readingDate | date:'medium' }}</span>
      </div>

      <div style="max-height: 320px; overflow-y: auto;">
        <table width="255px;" style="margin-bottom: 0px;">
          <tr>
            <td width="62%" style="color: #1c4966;">Reading Type</td>
            <td width="38%" align="right" style="color: #1c4966;">Value</td>
          </tr>
        </table>

        <hr style="margin-top: 3px; margin-bottom: 3px;">

        <table width="255px;" style="margin-bottom: 10px">
          <tr *ngFor="let reading of item.readings | keyvalue">
            <td width="62%" style="font-size: 14px;">
              <!-- Click toggles this reading type's chart; the pin's position (gpsLat / gpsLong) is plain text. -->
              <div *ngIf="isChartableReadingType(reading.key); else positionRow"
                   (click)="toggleReadingType($event, item, reading.key)" style="cursor: pointer;"
                   [style.color]="isReadingTypeSelected(item, reading.key) ? '#ccc127' : '#1c4966'">
                {{ reading.key }}
              </div>
              <ng-template #positionRow>
                <div style="color: #1c4966;">{{ reading.key }}</div>
              </ng-template>
            </td>
            <td width="38%" style="font-size: 14px; text-align: right;">{{ reading.value }}</td>
          </tr>
        </table>
      </div>

      <!-- One chart per selected reading type, stacked vertically in the order they were turned on.
           A tall stack scrolls inside the popup instead of growing it off the map. -->
      <div style="max-height: 336px; overflow-y: auto;">
      <div *ngFor="let chart of item.charts; trackBy: trackByReadingType"
           style="background-color: white; padding: 0px; border: 1px solid lightgray; border-radius: 3px; font-weight: 500; margin-bottom: 6px;">
        <ngx-charts-line-chart
            [view]="view"
            [scheme]="chartOptions.colorScheme"
            [results]="chart.data"
            [gradient]="chartOptions.gradient"
            [xAxis]="chartOptions.showXAxis"
            [yAxis]="chartOptions.showYAxis"
            [legend]="chartOptions.showLegend"
            [showXAxisLabel]="chartOptions.showXAxisLabel"
            [showYAxisLabel]="chartOptions.showYAxisLabel"
            [xAxisLabel]="chartOptions.xAxisLabel"
            [yAxisLabel]="chartOptions.yAxisLabel"
            [autoScale]="chartOptions.autoScale"
            [xAxisTickFormatting]="chartOptions.axisFormat"
            [legendTitle]="chartOptions.legendTitle"
            [timeline]="chartOptions.timeline"
            [yScaleMin]="chartOptions.scaleYMin"
            [yScaleMax]="chartOptions.scaleYMax">
        </ngx-charts-line-chart>
      </div>
      </div>
    </div>
  </div>
</ng-template>
